import { search } from "algolia-instant-search";
import {
  AlgoliaFilterBuilder,
  RT_PRODUCT_FEED_FILTERS,
} from "@theme/algolia-filter-builder";
import { VirtualWidget } from "@theme/algolia-virtual-widget";
const { stats, configure } = instantsearch.widgets;

if (!customElements.get("main-collection")) {
  class MainCollection extends HTMLElement {
    collectionId = this.dataset.collectionId;
    collectionHandle = this.dataset.collectionHandle;
    isB2B = this.dataset.b2b === "true";
    applyRtProductFeedFilter = this.dataset.rtProductFeedFilter === "true";
    filterClauses = new AlgoliaFilterBuilder({
      status: "ACTIVE",
      excludeBuilderBacking: { clause: 'NOT tags:"builder-backing"' },
    });

    connectedCallback() {
      if (this.applyRtProductFeedFilter) {
        if (this.isB2B) {
          this.filterClauses.set("rtProductFeed", RT_PRODUCT_FEED_FILTERS.b2b);
        } else {
          this.filterClauses.set(
            "rtProductFeed",
            RT_PRODUCT_FEED_FILTERS.retail,
          );
        }
      }

      this.selectElements();
      this.initializeSearch();
      this.bindFiltersToggle();
      this.bindSidebarBreakpointFix();

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => search.start());
      } else {
        search.start();
      }
    }

    selectElements() {
      const tag = this.querySelector("script#data");
      try {
        this.data = JSON.parse(tag.textContent);
      } catch {
        this.data = {};
      }
    }

    initializeSearch() {
      // Order matters: each widget's getWidgetSearchParameters receives the
      // previous one's output — family sets collection_ids, fitment appends
      // last, and filterClauses owns the final filters string.
      search.addWidgets([
        this.statsWidget,
        this.configureWidget,
        this.familyCollectionWidget,
        this.fitmentVehicleWidget,
      ]);
    }

    get statsWidget() {
      return stats({
        container: "#stats",
        templates: {
          text({ nbHits, page, hitsPerPage }) {
            if (nbHits === 0) return "No results";
            const first = page * hitsPerPage + 1;
            const last = Math.min((page + 1) * hitsPerPage, nbHits);
            return `${nbHits.toLocaleString()} results (showing ${first}–${last})`;
          },
        },
      });
    }

    get configureWidget() {
      return configure({
        distinct: true,
        facetingAfterDistinct: true,
        maxValuesPerFacet: 100,
      });
    }

    get familyCollectionWidget() {
      // Built synchronously here (not in onInit) — onInit runs after the FIRST
      // getWidgetSearchParameters call (see algolia-virtual-widget.js), so an
      // empty map at that point would silently drop the family filter on page
      // load for a ?family= deep link. The [data-cf-filter] cards are already
      // server-rendered by the time this getter runs (connectedCallback →
      // initializeSearch(), before search.start()), so building the map here
      // is safe and available for that first query.
      const handleToId = new Map(
        Array.from(document.querySelectorAll("[data-cf-filter]")).map((el) => [
          el.dataset.colHandle,
          el.dataset.colId,
        ]),
      );

      return new VirtualWidget({
        $$type: "algolia.family_collection_filter",
        uiStateKey: "family",
        buildSearchParameters: (searchParameters, family) => {
          const ids = family.handles
            ?.map((handle) => handleToId.get(handle))
            .filter(Boolean);
          const currentCollection = this.collectionId
            ? `collection_ids:${this.collectionId}`
            : null;
          const scope = ids?.length
            ? `(${ids.map((id) => `collection_ids:${id}`).join(" OR ")})`
            : currentCollection;
          if (scope) this.filterClauses.set("family", scope);
          else this.filterClauses.delete("family");

          return searchParameters.setQueryParameter(
            "filters",
            this.filterClauses.toString(),
          );
        },
      });
    }

    get fitmentVehicleWidget() {
      return new VirtualWidget({
        $$type: "algolia.fitment_vehicle_filter",
        uiStateKey: "fitment",
        buildSearchParameters: (searchParameters, fitment) => {
          // One encoding, one owner — see main-search.js. vehicleFilter returns ""
          // for a gid-less vehicle, which is exactly when no clause may apply. The
          // universal flag is what main has emitted live since 39a19e2.
          const clause = window.FyreFitment.vehicleFilter(fitment, { universal: true });
          if (clause) this.filterClauses.set("fitment", clause);
          else this.filterClauses.delete("fitment");

          return searchParameters.setQueryParameter(
            "filters",
            this.filterClauses.toString(),
          );
        },
      });
    }

    bindFiltersToggle() {
      const toggle = this.querySelector("#FiltersToggle");
      const overlay = this.querySelector("#FiltersOverlay");
      const sidebar = this.querySelector("#collection__sidebar");
      if (!toggle || !sidebar) return;

      const openFilters = () => {
        sidebar.classList.add("is-active");
        if (overlay) overlay.classList.add("is-active");
        toggle.setAttribute("aria-expanded", "true");
        document.body.style.overflow = "hidden";
      };

      const closeFilters = () => {
        sidebar.classList.remove("is-active");
        if (overlay) overlay.classList.remove("is-active");
        toggle.setAttribute("aria-expanded", "false");
        document.body.style.overflow = "";
      };

      toggle.addEventListener("click", openFilters);
      if (overlay) overlay.addEventListener("click", closeFilters);

      document.addEventListener("click", (e) => {
        if (e.target.closest('[data-action="close-filters"]')) closeFilters();
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeFilters();
      });

      this.closeFilters = closeFilters;
    }

    // The sidebar switches from in-flow layout to a fixed off-canvas drawer
    // at 991px. Crossing that breakpoint mid-resize otherwise animates the
    // drawer's transform transition, making it visibly slide across the
    // screen. Briefly hiding it kills that stray transition.
    bindSidebarBreakpointFix() {
      const sidebar = this.querySelector("#collection__sidebar");
      if (!sidebar) return;

      const mql = window.matchMedia("(max-width: 991px)");
      mql.addEventListener("change", (e) => {
        // The drawer's "is-active" state (and the body scroll lock it sets)
        // is mobile-only. Without this, resizing to desktop while the drawer
        // is open leaves body scroll locked and the sidebar stuck "active".
        if (!e.matches) this.closeFilters?.();

        sidebar.classList.add("no-transition");
        sidebar.style.display = "none";
        requestAnimationFrame(() => {
          sidebar.style.display = "";
          requestAnimationFrame(() => {
            sidebar.classList.remove("no-transition");
          });
        });
      });
    }
  }

  customElements.define("main-collection", MainCollection);
}
